(() => {
  'use strict';

  const formatLocalDate = (value) => {
    if (!value) return 'After payment confirmation';
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-AU', {
      day: 'numeric', month: 'long', year: 'numeric'
    }).format(date);
  };

  const toDateInputValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const initialise = (root) => {
    if (!root || root.dataset.mgInitialised === 'true') return;
    root.dataset.mgInitialised = 'true';

    const form = root.querySelector('.mg-gcp__form');
    if (!form) return;

    const variantId = form.querySelector('[data-mg-variant-id]');
    const variantInputs = [...form.querySelectorAll('[data-mg-variant]')];
    const deliveryInputs = [...form.querySelectorAll('[data-mg-delivery-choice]')];
    const timingInputs = [...form.querySelectorAll('[data-mg-send-timing]')];
    const nameInput = form.querySelector('[data-mg-recipient-name]');
    const recipientNameProperty = form.querySelector('[data-mg-recipient-name-property]');
    const emailInput = form.querySelector('[data-mg-delivery-email]');
    const recipientEmailProperty = form.querySelector('[data-mg-recipient-email-property]');
    const emailLabel = form.querySelector('[data-mg-email-label]');
    const emailHelp = form.querySelector('[data-mg-email-help]');
    const messageInput = form.querySelector('[data-mg-message]');
    const giftMessageProperty = form.querySelector('[data-mg-gift-message-property]');
    const messageCount = form.querySelector('[data-mg-message-count]');
    const dateField = form.querySelector('[data-mg-scheduled-field]');
    const dateInput = form.querySelector('[data-mg-send-on]');
    const deliveryDateProperty = form.querySelector('[data-mg-delivery-date-property]');
    const timezone = form.querySelector('[data-mg-timezone-offset]');
    const deliveryProperty = form.querySelector('[data-mg-delivery-property]');
    const submitButton = form.querySelector('[data-mg-submit]');
    const mobileSubmit = root.querySelector('[data-mg-mobile-submit]');
    const submitError = form.querySelector('[data-mg-submit-error]');

    const setText = (selector, text) => {
      root.querySelectorAll(selector).forEach((node) => { node.textContent = text; });
    };

    const showMedia = (mediaId) => {
      if (!mediaId) return;
      root.querySelectorAll('[data-mg-media-item]').forEach((item) => {
        const active = item.dataset.mgMediaItem === String(mediaId);
        item.hidden = !active;
        item.classList.toggle('is-active', active);
      });
      root.querySelectorAll('[data-mg-media-trigger]').forEach((trigger) => {
        const active = trigger.dataset.mgMediaTrigger === String(mediaId);
        trigger.classList.toggle('is-active', active);
        trigger.setAttribute('aria-pressed', String(active));
      });
    };

    const updateVariant = () => {
      const selected = variantInputs.find((input) => input.checked);
      if (!selected) return;
      variantId.value = selected.value;
      selected.closest('.mg-gcp__amount')?.classList.add('is-selected');
      variantInputs.filter((input) => input !== selected).forEach((input) => {
        input.closest('.mg-gcp__amount')?.classList.remove('is-selected');
      });
      setText('[data-mg-current-price], [data-mg-summary-price], [data-mg-submit-price], [data-mg-mobile-price], [data-mg-card-preview-price]', selected.dataset.price || '');
      if (selected.dataset.mediaId) showMedia(selected.dataset.mediaId);
      const disabled = selected.disabled;
      if (submitButton) submitButton.disabled = disabled;
      if (mobileSubmit) mobileSubmit.disabled = disabled;
    };

    const updateDelivery = () => {
      const selected = deliveryInputs.find((input) => input.checked);
      if (!selected) return;
      deliveryInputs.forEach((input) => {
        input.closest('[data-mg-delivery-card]')?.classList.toggle('is-selected', input === selected);
      });
      const purchaser = selected.value === 'purchaser';
      if (emailLabel) emailLabel.innerHTML = `${purchaser ? 'Your' : 'Recipient’s'} email <span aria-hidden="true">*</span>`;
      if (emailHelp) emailHelp.textContent = purchaser
        ? 'The voucher will be sent to you, ready to print or forward.'
        : 'The voucher will be sent directly to this address.';
      if (deliveryProperty) deliveryProperty.value = purchaser ? 'Send to purchaser' : 'Send to recipient';
      const summary = root.querySelector('[data-mg-summary-email]');
      if (summary) summary.textContent = emailInput?.value.trim() || (purchaser ? 'Your email' : 'Recipient’s email');
    };

    const updateTiming = () => {
      const selected = timingInputs.find((input) => input.checked);
      if (!selected || !dateField || !dateInput) return;
      timingInputs.forEach((input) => {
        input.closest('[data-mg-send-card]')?.classList.toggle('is-selected', input === selected);
      });
      const scheduled = selected.value === 'scheduled';
      dateField.hidden = !scheduled;
      dateInput.disabled = !scheduled;
      dateInput.required = scheduled;
      if (!scheduled) dateInput.value = '';
      const deliveryDate = scheduled
        ? (dateInput.value ? formatLocalDate(dateInput.value) : 'Choose a delivery date')
        : 'After payment confirmation';
      if (deliveryDateProperty) deliveryDateProperty.value = deliveryDate;
      setText('[data-mg-summary-date]', deliveryDate);
    };

    const updateName = () => {
      const recipientName = nameInput?.value.trim() || '';
      if (recipientNameProperty) recipientNameProperty.value = recipientName;
      setText('[data-mg-summary-name]', recipientName || 'Recipient’s name');
    };
    const updateEmail = () => {
      const purchaser = deliveryInputs.find((input) => input.checked)?.value === 'purchaser';
      const recipientEmail = emailInput?.value.trim() || '';
      if (recipientEmailProperty) recipientEmailProperty.value = recipientEmail;
      setText('[data-mg-summary-email]', recipientEmail || (purchaser ? 'Your email' : 'Recipient’s email'));
    };
    const updateMessageCount = () => {
      const giftMessage = messageInput?.value || '';
      if (giftMessageProperty) giftMessageProperty.value = giftMessage;
      if (messageCount) messageCount.textContent = String(giftMessage.length);
    };

    if (dateInput) {
      const today = new Date();
      const maxDate = new Date(today);
      maxDate.setDate(maxDate.getDate() + 90);
      dateInput.min = toDateInputValue(today);
      dateInput.max = toDateInputValue(maxDate);
      dateInput.addEventListener('change', updateTiming);
    }
    if (timezone) timezone.value = String(new Date().getTimezoneOffset());

    variantInputs.forEach((input) => input.addEventListener('change', updateVariant));
    deliveryInputs.forEach((input) => input.addEventListener('change', updateDelivery));
    timingInputs.forEach((input) => input.addEventListener('change', updateTiming));
    nameInput?.addEventListener('input', updateName);
    emailInput?.addEventListener('input', updateEmail);
    messageInput?.addEventListener('input', updateMessageCount);

    root.querySelectorAll('[data-mg-media-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => showMedia(trigger.dataset.mgMediaTrigger));
    });

    form.addEventListener('submit', (event) => {
      if (form.checkValidity()) {
        if (submitError) submitError.hidden = true;
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.querySelector('span')?.replaceChildren(document.createTextNode('Adding voucher…'));
        }
        if (mobileSubmit) mobileSubmit.disabled = true;
        return;
      }
      event.preventDefault();
      if (submitError) {
        submitError.textContent = 'Please complete the highlighted details before adding the gift voucher.';
        submitError.hidden = false;
      }
      const firstInvalid = form.querySelector(':invalid');
      firstInvalid?.focus();
      firstInvalid?.reportValidity();
    });

    mobileSubmit?.addEventListener('click', () => form.requestSubmit(submitButton || undefined));

    updateVariant();
    updateDelivery();
    updateTiming();
    updateName();
    updateEmail();
    updateMessageCount();

    const serverErrors = form.querySelector('[data-mg-server-errors]');
    serverErrors?.focus();
  };

  const initialiseWithin = (scope = document) => {
    scope.querySelectorAll('[data-mg-gift-card-product]').forEach(initialise);
    if (scope.matches?.('[data-mg-gift-card-product]')) initialise(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseWithin());
  } else {
    initialiseWithin();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseWithin(event.target));
})();
